import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { http, money, formatApiError, mesi, statusColors, API } from "@/lib/api";
import { normalizeBase } from "@/lib/reportCalc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Copy, Trash2, FileText, ChevronDown, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const now = new Date();
const empty = () => ({ owner_id: "", apartment_id: "", month: now.getMonth() + 1, year: now.getFullYear(), management_percent: 15, management_base: "netto_dirette" });
const FINAL = ["controllato", "inviato", "pagato"];

function FinBadge({ mv, label }) {
  if (!mv) return null;
  const ok = mv.financial_status === "incassato";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid={`fin-badge-${label}`}>{label} {ok ? "incassata" : "da incassare"}</span>;
}

export default function Rendiconti() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], years: [] });
  const [owners, setOwners] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [year, setYear] = useState(now.getFullYear());
  const [fMonth, setFMonth] = useState("all");
  const [fApt, setFApt] = useState("all");
  const [fOwner, setFOwner] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [openMonths, setOpenMonths] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const [r, o, a] = await Promise.all([http.get("/owner-reports/index", { params: { year } }), http.get("/owners"), http.get("/apartments")]);
    setData(r.data); setOwners(o.data); setApartments(a.data); setLoading(false);
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line
  useEffect(() => { if (params.get("new") === "1") { setForm(empty()); setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, [params, setParams]);

  const filtered = useMemo(() => data.items.filter((r) =>
    (fMonth === "all" || r.month === Number(fMonth)) && (fApt === "all" || r.apartment_id === fApt) &&
    (fOwner === "all" || r.owner_id === fOwner) && (fStatus === "all" || r.status === fStatus)), [data, fMonth, fApt, fOwner, fStatus]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((r) => { (g[r.month] = g[r.month] || []).push(r); });
    return Object.keys(g).map(Number).sort((a, b) => b - a).map((m) => {
      const rows = g[m].sort((a, b) => (a.apartment_name || "").localeCompare(b.apartment_name || ""));
      const missing = rows.filter((r) => [r.finance?.provvigione, r.finance?.pulizie_fatturate].some((mv) => mv && mv.financial_status === "da_incassare")).length;
      return { month: m, rows, final: rows.filter((r) => FINAL.includes(r.status)).length, bozze: rows.filter((r) => r.status === "bozza").length, missing,
        tot: rows.reduce((s, r) => ({ net: s.net + r.total_net, fee: s.fee + r.management_fee, res: s.res + r.owner_result }), { net: 0, fee: 0, res: 0 }) };
    });
  }, [filtered]);

  const isOpen = (m) => openMonths[m] ?? (groups[0]?.month === m || (year === now.getFullYear() && m === now.getMonth() + 1));
  const toggle = (m) => setOpenMonths({ ...openMonths, [m]: !isOpen(m) });

  const create = async (e) => {
    e.preventDefault();
    try {
      const apt = apartments.find((x) => x.id === form.apartment_id);
      const payload = { ...form, management_percent: Number(form.management_percent), owner_id: form.owner_id || apt?.owner_id };
      if (!payload.owner_id) return toast.error("Seleziona un proprietario");
      const { data } = await http.post("/owner-reports", payload);
      toast.success("Rendiconto creato"); setOpen(false); nav(`/rendiconti/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dup = async (id) => { const { data } = await http.post(`/owner-reports/${id}/duplicate`); toast.success("Duplicato (mese successivo, solo configurazioni)"); nav(`/rendiconti/${data.id}`); };
  const del = async (id) => { if (!window.confirm("Eliminare il rendiconto?")) return; await http.delete(`/owner-reports/${id}`); load(); };
  // XHR autenticato + blob: la navigazione diretta all'endpoint perde il cookie di sessione
  const openPdf = async (id) => {
    try {
      const resp = await http.get(`/owner-reports/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore generazione PDF"); }
  };


  return (
    <div className="space-y-6" data-testid="rendiconti-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Centro di comando economico</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Rendiconti Proprietari</h1>
        </div>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]" data-testid="new-report-btn"><Plus className="h-4 w-4 mr-1" /> Nuovo rendiconto</Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="rep-filters">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="w-[100px]" data-testid="rep-f-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[...new Set([...(data.years || []), now.getFullYear(), now.getFullYear() + 1])].sort((a, b) => b - a).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
        <Select value={fMonth} onValueChange={setFMonth}><SelectTrigger className="w-[140px]" data-testid="rep-f-month"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i mesi</SelectItem>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
        <Select value={fApt} onValueChange={setFApt}><SelectTrigger className="w-[180px]" data-testid="rep-f-apt"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli appartamenti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fOwner} onValueChange={setFOwner}><SelectTrigger className="w-[180px]" data-testid="rep-f-owner"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i proprietari</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-[140px]" data-testid="rep-f-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Ogni stato</SelectItem>{["bozza", "controllato", "inviato", "pagato"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
      </div>

      {loading && <div className="text-slate-400 text-sm">Caricamento...</div>}
      {!loading && groups.length === 0 && <div className="text-center text-slate-400 py-10">Nessun rendiconto per i filtri selezionati.</div>}

      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.month} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`month-group-${g.month}`}>
            <button onClick={() => toggle(g.month)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50" data-testid={`month-toggle-${g.month}`}>
              {isOpen(g.month) ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              <div className="font-display text-lg font-bold text-[var(--teal-deep)] w-32">{mesi[g.month]} {year}</div>
              <div className="flex gap-1.5 flex-wrap text-[11px]">
                <Badge variant="outline" className="border-slate-200 text-slate-600">{g.rows.length} rendiconti</Badge>
                {g.final > 0 && <Badge variant="outline" className="border-emerald-300 text-emerald-800">{g.final} finalizzati</Badge>}
                {g.bozze > 0 && <Badge variant="outline" className="border-amber-300 text-amber-800">{g.bozze} bozze</Badge>}
                {g.missing > 0 && <Badge variant="outline" className="border-rose-300 text-rose-800 gap-1"><AlertCircle className="h-3 w-3" />{g.missing} con incassi mancanti</Badge>}
              </div>
              <div className="ml-auto hidden md:flex gap-5 text-xs text-slate-500 tabular-nums">
                <span>Ricavi <b className="text-slate-800">{money(g.tot.net)}</b></span>
                <span>Provvigioni <b className="text-slate-800">{money(g.tot.fee)}</b></span>
                <span>Netto prop. <b className="text-slate-800">{money(g.tot.res)}</b></span>
              </div>
            </button>
            {isOpen(g.month) && (
              <div className="border-t border-slate-100 divide-y divide-slate-100">
                {g.rows.map((r) => (
                  <div key={r.id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 hover:bg-[color:var(--teal-light)]/40" data-testid={`report-${r.id}`}>
                    <Link to={`/rendiconti/${r.id}`} className="flex-1 min-w-0 flex items-center gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{r.apartment_name}</div>
                        <div className="text-xs text-slate-500 truncate">{r.owner_name}</div>
                      </div>
                      <Badge variant="outline" className={`${statusColors[r.status]} capitalize`}>{r.status}</Badge>
                      <div className="hidden md:flex gap-1.5">
                        <FinBadge mv={r.finance?.provvigione} label="Provvigione" />
                        <FinBadge mv={r.finance?.pulizie_fatturate} label="Pulizie" />
                      </div>
                    </Link>
                    <div className="grid grid-cols-3 gap-3 text-xs tabular-nums sm:w-[330px]">
                      <div><div className="text-slate-400">Ricavi</div><div className="font-semibold">{money(r.total_net)}</div></div>
                      <div><div className="text-slate-400">Provvigione</div><div className="font-semibold">{money(r.management_fee)}</div></div>
                      <div><div className="text-slate-400">Netto prop.</div><div className="font-semibold text-[var(--terracotta)]">{money(r.owner_result)}</div></div>
                    </div>
                    <div className="flex gap-0.5 justify-end">
                      <button onClick={() => dup(r.id)} data-testid={`dup-report-${r.id}`} className="p-2 text-slate-500 hover:text-teal-700" title="Duplica mese successivo"><Copy className="h-4 w-4" /></button>
                      <button onClick={() => openPdf(r.id)} data-testid={`pdf-report-${r.id}`} className="p-2 text-slate-500 hover:text-teal-700" title="PDF (generato su richiesta)"><FileText className="h-4 w-4" /></button>
                      <button onClick={() => del(r.id)} className="p-2 text-slate-500 hover:text-rose-600" data-testid={`del-report-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo rendiconto</DialogTitle></DialogHeader>
          <form onSubmit={create} className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Appartamento</Label>
              <Select value={form.apartment_id} onValueChange={(v) => { const a = apartments.find((x) => x.id === v); setForm({ ...form, apartment_id: v, owner_id: a?.owner_id || form.owner_id, management_percent: a?.management_percent ?? form.management_percent, management_base: a?.management_base ? normalizeBase(a.management_base) : form.management_base }); }}>
                <SelectTrigger data-testid="rep-apt"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="col-span-2"><Label>Proprietario</Label>
              <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                <SelectTrigger data-testid="rep-owner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Mese</Label>
              <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: Number(v) })}>
                <SelectTrigger data-testid="rep-month"><SelectValue /></SelectTrigger>
                <SelectContent>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Anno</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} data-testid="rep-year" /></div>
            <div><Label>% gestione</Label><Input type="number" step="0.01" value={form.management_percent} onChange={(e) => setForm({ ...form, management_percent: e.target.value })} data-testid="rep-mgmt-pct" /></div>
            <div><Label>Base calcolo</Label>
              <Select value={form.management_base} onValueChange={(v) => setForm({ ...form, management_base: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="netto_dirette">Netto OTA + Dirette</SelectItem><SelectItem value="netto_ota">Netto OTA</SelectItem>
                  <SelectItem value="lordo">Lordo</SelectItem><SelectItem value="custom">Importo personalizzato</SelectItem>
                </SelectContent></Select>
            </div>
            <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="rep-submit">Crea</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
